import {
  Button,
  Checkbox,
  DatePickerDropdown,
  HSpacer,
  IconButton,
  Input,
  MenuItem,
  Modal,
  Select,
  Text,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { InfoTooltip } from '@/components/DesignSystem/InfoTooltip/InfoTooltip';
import { Table, TableRow } from '@/components/DesignSystem/Table/Table';
import { QueryKeys } from '@/constants/QueryKeys';
import { useColor } from '@/hooks/useColor';
import {
  DeleteBenefitConfirmDialog,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/DeleteBenefitConfirmDialog';
import {
  RewardsTier,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/RewardsTier";
import {
  RewardsTierBenefitModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/RewardsTierBenefitModal';
import {
  TierExclusionsSection,
} from "@/pages/Admin/HierarchyOfRetailers/Retailer/RewardsPrograms/TierExclusionsSection";
import { CategoryMappingApi } from '@/utilities/api/CategoryMappingApi';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RewardsProgramApi } from "@/utilities/api/RewardsProgramApi";
import { RewardsProgramEndpoint } from '@api/endpoints/RewardsProgramEndpoint';
import { ApiCategory } from '@api/interfaces';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Box, CircularProgress, Divider, Stack, useTheme } from '@mui/material';
import { GrainTransactionImportType } from '@shared/enums/GrainTransactionImportType';
import { RewardsProgramType } from '@shared/enums/RewardsProgramType';
import { RewardsType } from '@shared/enums/RewardsType';
import { RewardsUom } from '@shared/enums/RewardsUom';
import { SharedConfig } from "@shared/SharedConfig";
import {
  businessToday,
  formatLocalDate,
  formatMediumDate,
  getDurationInDays,
  getJsDateFromString,
  pick,
} from '@shared/utilities';
import i18next from 'i18next';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { RewardsTiersModal } from './RewardsTiersModal';

const DefaultProgramName = 'Rewards & Loyalty Program';

export type SubcategoryRewards = Record<
  string,
  {
    hideOnDashboard: boolean,
    loyaltyUom: RewardsUom,
    rewardsType: RewardsType,
    rewardsValue: number,
  }
>;

export type Benefit = {
  description: string,
  name: string,
  termsUrl: string | null,
  tier: Tier,
}

type Program = {
  categoryIdsExcludedFromSegmentSpend?: string[],
  categoryIdsExcludedFromTierSpend?: string[],
  endDate: string | null,
  grainTransactionImportType: GrainTransactionImportType | undefined,
  grantBothProgramAndPromotionPoints: boolean,
  name: string,
  pointsExpirationDate: string | null,
  startDate: string | null,
  tiers: Tier[],
}

export type Tier = {
  minimumDollars: number,
  minimumSegments: number,
  name: string,
  segments: Segment[],
}

export type Segment = {
  categoryId: string,
  hideOnDashboard: boolean,
  loyaltyUom: RewardsUom | null,
  minimumHurdle: number,
  rewardsType: RewardsType | null,
  rewardsValue: number | null,
  subcategoryRewards: SubcategoryRewards | null,
  uom: RewardsUom,
}

type AgronomyCreateBody = Omit<RewardsProgramEndpoint.Create.Request, 'retailerId'>;

interface RewardsProgramModalProps {
  existingProgram?: ApiRewardsProgram,
  forcedStartDate?: string,
  onClose: () => void,
  onCreateAgronomy?: (rewardsProgram: AgronomyCreateBody) => void,
  onUpdate?: (updates: RewardsProgramEndpoint.Update.Request) => void,
  retailerId: string,
}

const {
  rewardsMinProgramDurationInDays,
} = SharedConfig.settings;

export const RewardsProgramModal = ({
  existingProgram,
  forcedStartDate,
  onClose,
  onCreateAgronomy,
  onUpdate,
  retailerId,
}: RewardsProgramModalProps) => {

  const initialProgramValues = useMemo(() => {
    const endDate = forcedStartDate
      ? DateTime
        .fromJSDate(getJsDateFromString(forcedStartDate))
        .plus({ years: 1 })
        .minus({ days: 1 })
        .toFormat('yyyy-MM-dd')
      : null;
    let programValues: Program = {
      endDate,
      grainTransactionImportType: undefined,
      grantBothProgramAndPromotionPoints: false,
      name: DefaultProgramName,
      pointsExpirationDate: endDate,
      startDate: forcedStartDate ?? null,
      tiers: [],
    };
    if (existingProgram) {
      programValues = {
        ...programValues,
        ...pick(existingProgram, [
          'categoryIdsExcludedFromSegmentSpend',
          'categoryIdsExcludedFromTierSpend',
          'endDate',
          'grainTransactionImportType',
          'grantBothProgramAndPromotionPoints',
          'name',
          'pointsExpirationDate',
          'startDate',
        ]),
        tiers: existingProgram.tiers?.map((tier) => (
          { ...tier, segments: tier.segments ?? [] }
        )) ?? [],
      };
    }
    return programValues;
  }, [existingProgram, forcedStartDate]);

  const theme = useTheme();
  const [showRewardsTiersModal, setShowRewardsTiersModal] = useState(false);
  const [showRewardsTierBenefitsModal, setShowRewardsTierBenefitsModal] = useState(false);
  const [showDeleteBenefitDialog, setShowDeleteBenefitDialog] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<ApiCategory[]>([]);
  const [initialSelectedCategories, setInitialSelectedCategories] = useState<ApiCategory[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [program, setProgram] = useState<Program>(initialProgramValues);
  const [selectedCategoryMapping, setSelectedCategoryMapping] = useState<ApiCategoryMapping>();

  const { data: hasActiveUsers, isFetching: isCheckingForActiveUsers } = useQuery(
    [QueryKeys.GET_REWARDS_PROGRAM_HAS_ACTIVE_USERS, existingProgram?.id],
    () => RewardsProgramApi.hasActiveUsers(existingProgram?.id ?? ''),
    { enabled: !!existingProgram?.id },
  );

  const { data: retailer } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, retailerId],
    () => RetailerApi.get(retailerId),
  );

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, retailerId],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({ retailerId }),
  );

  const [benefitToEdit, setBenefitToEdit] = useState<Benefit>();
  const existingBenefits = existingProgram?.tiers?.flatMap(
    (tier) => (tier.benefits ?? []).map((benefit) => ({ ...benefit, tier })),
  ) ?? [];

  const [initialBenefits] = useState(existingBenefits);
  const [benefits, setBenefits] = useState<Benefit[]>(existingBenefits?.map((benefit) => ({
    ...benefit,
    tier: { ...benefit.tier, segments: benefit.tier.segments ?? [] },
  })) ?? []);
  const { getColor } = useColor();

  const isViewing = !!existingProgram && !isEditing;
  const canEdit = (
    !!existingProgram?.endDate
    && existingProgram?.endDate >= businessToday()
  );
  const tiersWithSelectedCategories = program.tiers.map((tier) => ({
    ...tier,
    segments: tier.segments
      .filter(({ categoryId }) => (
        selectedCategories.map(({ id }) => id).includes(categoryId)
      ))
      .map((segment) => {
        const newSegment = {
          ...segment,
          subcategoryRewards: segment.subcategoryRewards
            ? Object.fromEntries(
              Object.entries(segment.subcategoryRewards).filter(([subcategoryId, _]) => (
                selectedCategories.map(({ id }) => id).includes(subcategoryId)
              )),
            )
            : null,
        };
        if (
          newSegment.subcategoryRewards
          && Object.keys(newSegment.subcategoryRewards).length === 0
        ) {
          newSegment.subcategoryRewards = null;
        }
        return newSegment;
      }),
  }));

  const { data: categories } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORIES, retailerId],
    async () => {
      const categories = await RetailerApi.listCategories(
        retailerId,
        { mappingId: selectedCategoryMapping!.id },
      );
      let initialSelectedCategories: ApiCategory[] = categories;
      if (existingProgram) {
        const categoryIds = [...new Set(existingProgram.tiers?.flatMap(
          (tier) => tier.segments?.flatMap((segment) => (
            [segment.categoryId, ...Object.keys(segment.subcategoryRewards ?? {})]
          )),
        ) ?? [])];
        initialSelectedCategories = categories.filter(
          (category) => categoryIds.includes(category.id),
        );
      }
      setInitialSelectedCategories(initialSelectedCategories);
      setSelectedCategories(initialSelectedCategories);
      return categories;
    },
    { enabled: !!selectedCategoryMapping },
  );

  const { data: categoryMappings } = useQuery(
    [QueryKeys.GET_RETAILER_CATEGORY_MAPPINGS, retailerId],
    async () => {
      const mappings = await CategoryMappingApi.list(retailerId);
      if (existingProgram) {
        const selectedMapping = mappings.find(
          (mapping) => mapping.programId === existingProgram.id,
        );
        setSelectedCategoryMapping(selectedMapping);
      }
      return mappings;
    },
  );

  const programDateRangeError = useMemo(() => {
    const startDate = program.startDate;
    const endDate = program.endDate;
    if (!endDate || !startDate) {
      return;
    }
    if (startDate >= endDate) {
      return 'Start date must be before end date';
    }
    const programDurationInDays = getDurationInDays(startDate, endDate);
    if (programDurationInDays < rewardsMinProgramDurationInDays) {
      return 'Program duration must be at least 12 months';
    }
    if (
      !existingProgram
      && latestProcessedOrderDate
      && startDate <= latestProcessedOrderDate
    ) {
      return `Start date must be after latest processed order date (${latestProcessedOrderDate})`;
    }
  }, [existingProgram, latestProcessedOrderDate, program.endDate, program.startDate]);

  const pointsExpirationDateError = useMemo(() => {
    const pointsExpirationDate = program.pointsExpirationDate;
    const endDate = program.endDate;
    if (!endDate || !pointsExpirationDate) {
      return;
    }
    if (pointsExpirationDate < endDate) {
      return 'Points expiration must be on or after end date';
    }
    if (pointsExpirationDate <= businessToday()) {
      return 'Points expiration must be in the future';
    }
  }, [program.endDate, program.pointsExpirationDate]);

  const startJsDate = program.startDate
    ? getJsDateFromString(program.startDate)
    : undefined;
  const endJsDate = program.endDate
    ? getJsDateFromString(program.endDate)
    : undefined;
  const pointsExpirationJsDate = program.pointsExpirationDate
    ? getJsDateFromString(program.pointsExpirationDate)
    : undefined;

  const areBenefitsChanged = !isEqual(initialBenefits, benefits);
  const areSelectedCategoriesChanged = !isEqual(initialSelectedCategories, selectedCategories);
  const areTiersChanged = !!existingProgram?.tiers && program.tiers.some((tier) => {
    const existingTier = existingProgram.tiers!.find((t) => t.name === tier.name);
    return !existingTier
      || existingTier.minimumDollars !== tier.minimumDollars
      || existingTier.minimumSegments !== tier.minimumSegments
      || !isEqual(existingTier.segments, tier.segments)
      || areSelectedCategoriesChanged;
  });
  const isChanged = (
    program.name !== existingProgram?.name
    || program.endDate !== existingProgram?.endDate
    || program.pointsExpirationDate !== existingProgram.pointsExpirationDate
    || program.startDate !== existingProgram?.startDate
    || program.grainTransactionImportType !== (
      existingProgram?.grainTransactionImportType ?? undefined
    )
    || program.grantBothProgramAndPromotionPoints
      !== existingProgram?.grantBothProgramAndPromotionPoints
    || areTiersChanged
    || areBenefitsChanged
    || areSelectedCategoriesChanged
    || !isEqual(
      program.categoryIdsExcludedFromSegmentSpend,
      existingProgram.categoryIdsExcludedFromSegmentSpend,
    )
    || !isEqual(
      program.categoryIdsExcludedFromTierSpend,
      existingProgram.categoryIdsExcludedFromTierSpend,
    )
  );
  const categoriesInTiers = program.tiers
    .flatMap((tier) => tier.segments)
    .flatMap(({ categoryId, subcategoryRewards }) => (
      [categoryId, ...Object.keys(subcategoryRewards ?? {})]
    ));
  const areAllSelectedCategoriesIncluded = selectedCategories.every((category) => (
    categoriesInTiers.includes(category.id)
  ));
  const areRewardsValid = program.tiers.every(({ segments }) => (
    segments.every(({ rewardsType, rewardsValue, subcategoryRewards }) => (
      !((rewardsType !== null) !== (rewardsValue !== null))
      && !(rewardsType !== null && !!subcategoryRewards)
    ))
  ));
  const isValid = (
    !!program.name.trim()
    && !!program.endDate
    && !!program.startDate
    && !!program.pointsExpirationDate
    && !programDateRangeError
    && !pointsExpirationDateError
    && program.tiers.length > 0
    && selectedCategories.length > 0
    && isChanged
    && areAllSelectedCategoriesIncluded
    && areRewardsValid
    && !!selectedCategoryMapping
    && (retailer?.isErpIntegrationEnabled ? !!program.grainTransactionImportType : true)
  );

  const handleSave = async () => {
    onClose();
    if (isEditing) {
      const updateBody: RewardsProgramEndpoint.Update.Request = {};
      if (program.name !== existingProgram?.name) {
        updateBody.name = program.name;
      }
      if (program.startDate !== existingProgram?.startDate) {
        updateBody.startDate = program.startDate!;
      }
      if (program.endDate !== existingProgram?.endDate) {
        updateBody.endDate = program.endDate!;
      }
      if (program.pointsExpirationDate !== existingProgram?.pointsExpirationDate) {
        updateBody.pointsExpirationDate = program.pointsExpirationDate!;
      }
      if (program.grantBothProgramAndPromotionPoints
        !== existingProgram?.grantBothProgramAndPromotionPoints) {
        updateBody.grantBothProgramAndPromotionPoints = program.grantBothProgramAndPromotionPoints;
      }
      if (
        program.grainTransactionImportType !== (
          existingProgram?.grainTransactionImportType ?? undefined
        )
      ) {
        updateBody.grainTransactionImportType = program.grainTransactionImportType;
      }
      if (!isEqual(
        program.categoryIdsExcludedFromSegmentSpend,
        existingProgram?.categoryIdsExcludedFromSegmentSpend,
      )) {
        updateBody.categoryIdsExcludedFromSegmentSpend =
          program.categoryIdsExcludedFromSegmentSpend;
      }
      if (!isEqual(
        program.categoryIdsExcludedFromTierSpend,
        existingProgram?.categoryIdsExcludedFromTierSpend,
      )) {
        updateBody.categoryIdsExcludedFromTierSpend = program.categoryIdsExcludedFromTierSpend;
      }

      if (areTiersChanged) {
        if (areSelectedCategoriesChanged) {
          const selectedSegmentIds = selectedCategories.map((category) => category.id);
          program.tiers.forEach((tier) => {
            tier.segments = tier.segments
              .filter((segment) => (
                selectedSegmentIds.includes(segment.categoryId)
              ))
              .map((segment) => ({
                ...segment,
                subcategoryRewards: segment.subcategoryRewards
                  ? Object.fromEntries(
                    Object.entries(segment.subcategoryRewards).filter(([subcategoryId, _]) => (
                      selectedSegmentIds.includes(subcategoryId)
                    ),
                    ))
                  : null,
              }));
          });
        }
        updateBody.tiers = program.tiers as RewardsProgramEndpoint.Create.Tier[];
      }
      if (areBenefitsChanged) {
        const tiers = program.tiers.map((tier) => {
          const tierBenefits = benefits.filter((benefit) => benefit.tier.name === tier.name);
          return { ...tier, benefits: tierBenefits };
        });
        updateBody.tiers = tiers as RewardsProgramEndpoint.Create.Tier[];
      }
      onUpdate?.(updateBody);
    } else {
      if (!selectedCategoryMapping) {
        return;
      }
      const tiers = tiersWithSelectedCategories.map((tier) => {
        const tierBenefits = benefits.filter((benefit) => benefit.name === tier.name);
        return {
          ...tier,
          benefits: tierBenefits,
          segments: tier.segments as RewardsProgramEndpoint.Create.Segment[],
        };
      });
      const createBody = {
        categoryMappingId: selectedCategoryMapping.id,
        grantBothProgramAndPromotionPoints: program.grantBothProgramAndPromotionPoints,
        endDate: program.endDate!,
        grainTransactionImportType: program.grainTransactionImportType || undefined,
        name: program.name,
        pointsExpirationDate: program.pointsExpirationDate!,
        startDate: program.startDate!,
        tiers,
      };
      onCreateAgronomy?.(createBody);
    }
  };

  const ProgramDateRangeInputs = (
    (isViewing || hasActiveUsers) ? (
      <Stack direction="row">
        <Stack>
          <Text category="label-large">
            Start Date
          </Text>
          <VSpacer size="2" />
          <Text category="body-large">
            {formatMediumDate(startJsDate!, i18next)}
          </Text>
        </Stack>
        <HSpacer size="7" />
        <Stack>
          <Text category="label-large">
            End Date
          </Text>
          <VSpacer size="2" />
          <Text category="body-large">
            {formatMediumDate(endJsDate!, i18next)}
          </Text>
        </Stack>
      </Stack>
    ) : (
      <Stack direction="row" width={464}>
        <DatePickerDropdown
          disabled={!!forcedStartDate}
          errorMessage={programDateRangeError}
          label="Start Date"
          onChange={(date) => (
            setProgram({ ...program, startDate: date ? formatLocalDate(date) : null })
          )}
          testID="start-date-input"
          value={startJsDate}
        />
        <HSpacer size="7" />
        <DatePickerDropdown
          errorMessage={programDateRangeError && ' '}
          label="End Date"
          onChange={(date) => (
            setProgram({ ...program, endDate: date ? formatLocalDate(date) : null })
          )}
          testID="end-date-input"
          value={endJsDate}
        />
      </Stack>
    )
  );

  const PointsExpirationDateInputs = (
    (isViewing) ? (
      <Text category="body-large">
        {formatMediumDate(pointsExpirationJsDate!, i18next)}
      </Text>
    ) : (
      <DatePickerDropdown
        errorMessage={pointsExpirationDateError}
        label="Points Expiration Date"
        onChange={(date) => (
          setProgram({ ...program, pointsExpirationDate: date ? formatLocalDate(date) : null })
        )}
        testID="points-expiration-date-input"
        value={pointsExpirationJsDate}
        width={464}
      />
    )
  );

  const TierBenefitsTable = () => {
    if (!categories) {
      return null;
    }
    return (
      <>
        <Table
          headers={['Tier Name', 'Benefit Title', 'Benefit Description', '']}
        >
          {benefits.map((benefit) => (
            <TableRow
              key={benefit.name}
              values={[
                benefit.tier.name,
                benefit.name,
                benefit.description,
                <Stack direction="row" justifyContent="flex-end" key={`${benefit.name}-actions`}>
                  {!!benefit.termsUrl && (
                    <>
                      <TextLink
                        href={benefit.termsUrl}
                        sx={{ color: getColor('info', 5) }}
                        target='_blank'
                        testID={`${benefit.name}-terms-url`}
                      >
                        Terms
                      </TextLink>
                      <HSpacer size="12" />
                    </>
                  )}
                  <Box width="96px">
                    {isViewing ? null : (
                      <IconButton
                        color="primary"
                        key={`${benefit.name}-edit`}
                        onClick={() => {
                          setShowRewardsTierBenefitsModal(true);
                          setBenefitToEdit(benefit);
                        }}
                        testID={`${benefit.name}-edit-button`}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {isViewing ? null : (
                      <IconButton
                        color="primary"
                        key={`${benefit.name}-delete`}
                        onClick={() => {
                          setBenefitToEdit(benefit);
                          setShowDeleteBenefitDialog(true);
                        }}
                        testID={`${benefit.name}-delete-button`}
                      >
                        <DeleteOutline />
                      </IconButton>
                    )}
                  </Box>
                </Stack>,
              ]}
            />
          ))}
        </Table>
        {benefits.length === 0 && (
          <Box
            alignItems="center"
            bgcolor={theme.palette.background.paper}
            display="flex"
            justifyContent="center"
            py="64px"
          >
            <Text category="body-xlarge">
              Add custom benefits per each rewards tier
            </Text>
          </Box>
        )}
      </>
    );
  };

  const parentCategories = categories?.filter(({ parentId }) => !parentId);
  const orderedCategories = parentCategories?.flatMap((parent) => {
    const children = categories!.filter(({ parentId }) => parentId === parent.id);
    return [parent, ...children];
  });

  const CategoryMappingSelect = (
    <Select
      label="Category Mapping"
      onChangeValue={(id) => {
        const categoryMapping = categoryMappings?.find((mapping) => mapping.id === id);
        if (categoryMapping) {
          setSelectedCategoryMapping(categoryMapping);
        }
      }}
      placeholder={selectedCategoryMapping?.name}
      readOnly={isViewing || hasActiveUsers}
      renderValue={() => selectedCategoryMapping?.name}
      testID="category-mapping-select"
      value={selectedCategoryMapping?.id}
      width={464}
    >
      {(categoryMappings ?? [])
        .filter(({ programType }) => programType === RewardsProgramType.Agronomy)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((category) => (
          <MenuItem
            disabled={
              !!category.programId && category.programId !== selectedCategoryMapping?.programId
            }
            key={category.id}
            testID={`${category.id}-item`}
            value={category.id}
          >
            {category.name}
          </MenuItem>
        ))}
    </Select>
  );

  const CategorySelect = (
    <Select
      label="Segments"
      multiple
      onChange={(e) => {
        if (categories) {
          const categoryIds = e.target.value as string[];
          const selectedCategoryIds = selectedCategories.map(({ id }) => id);
          const isRemoval = categoryIds.length < selectedCategories.length;
          const categoryIdToUpdate = isRemoval
            ? selectedCategoryIds.find((id) => !categoryIds.includes(id))!
            : categoryIds.find((id) => !selectedCategoryIds.includes(id))!;
          const categoryToUpdate = categories.find(({ id }) => id === categoryIdToUpdate)!;
          const isSubcategory = !!categoryToUpdate.parentId;
          const isParentSelected = (
            isSubcategory
            && selectedCategoryIds.includes(categoryToUpdate.parentId!)
          );
          let newSelectedCategoryIds = [...categoryIds];

          if (isSubcategory) {
            if (!isRemoval && !isParentSelected) {
              newSelectedCategoryIds.push(categoryToUpdate.parentId!);
            }
          } else {
            const subcategories = categories.filter(({ parentId }) => (
              parentId === categoryIdToUpdate
            ));
            if (isRemoval) {
              newSelectedCategoryIds = newSelectedCategoryIds.filter((id) => (
                !subcategories.map(({ id }) => id).includes(id)),
              );
            } else {
              newSelectedCategoryIds.push(...subcategories.map(({ id }) => id));
            }
          }
          const newSelectedCategories = categories.filter(({ id }) => (
            newSelectedCategoryIds.includes(id)
          ));
          setSelectedCategories(newSelectedCategories.sort(
            (a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
          );
        }
      }}
      placeholder={selectedCategories.map((category) => category.name).join(', ')}
      readOnly={isViewing || hasActiveUsers}
      renderValue={() => selectedCategories.map((category) => category.name).sort().join(', ')}
      testID="segments-select"
      value={selectedCategories.map(({ id }) => id)}
      width={464}
    >
      {(orderedCategories ?? []).map((category) => (
        <MenuItem
          key={category.id}
          testID={`${category.id}-item`}
          value={category.id}
        >
          {!!category.parentId && (
            <HSpacer size="8" />
          )}
          <Checkbox
            checked={selectedCategories.map(({ id }) => id).includes(category.id)}
            testID={`segments-select-${category.id}-checkbox`}
          />
          {category.name}
        </MenuItem>
      ))}
    </Select>
  );

  let modalTitle: string;
  if (isEditing) {
    modalTitle = 'Edit Program';
  } else if (isViewing) {
    modalTitle = 'View Program';
  } else {
    modalTitle = 'Add Program';
  }

  return (
    <>
      <Modal
        acceptButton={!isViewing ? (props) => (
          <Button
            {...props}
            disabled={!isValid}
            onClick={handleSave}
            testID="rewards-program-modal-save-button"
          >
            Save
          </Button>
        ) : undefined}
        cancelButton={(props) => (
          <Button
            {...props}
            onClick={onClose}
            testID="rewards-program-modal-cancel-button"
          >
            {isViewing ? 'Close' : 'Cancel'}
          </Button>
        )}
        headerAccessoryRight={canEdit && !isEditing && (
          <Button
            disabled={isCheckingForActiveUsers}
            onClick={() => setIsEditing(true)}
            startIcon={<EditOutlined />}
            sx={{ paddingX: '20px' }}
            testID="rewards-program-modal-edit-button"
          >
            Edit
          </Button>
        )}
        largeModal
        onClose={onClose}
        open
        testID="rewards-program-modal"
        title={modalTitle}
      >
        <Text>
          Define the rewards program that is being offered by this retailer to their customers.
          The program will restart at the end of each period. Customers must re-earn the
          tier that they are currently in each period or will be moved to the tier that they
          have earned at the end of the period.
        </Text>
        <VSpacer size="8" />
        <Stack alignItems="center" direction="row">
          <Text category="title-medium">
            Campaign Name
          </Text>
          <HSpacer size="3" />
          <InfoTooltip>
            Customize the name of the retailer rewards program
          </InfoTooltip>
        </Stack>
        <VSpacer size="5" />
        <Input
          disabled={isViewing}
          label="Campaign Name"
          onChangeText={(name) => setProgram({ ...program, name })}
          testID="rewards-program-name-input"
          value={program.name}
          width={464}
        />
        <VSpacer size="9" />
        <Stack alignItems="center" direction="row">
          <Text category="title-medium">
            Program Period
          </Text>
          <HSpacer size="3" />
          <InfoTooltip>
            Select the time period that the retailer rewards program is valid for
          </InfoTooltip>
        </Stack>
        <VSpacer size="6" />
        {latestProcessedOrderDate === undefined ? (
          <CircularProgress />
        ) : ProgramDateRangeInputs}
        <VSpacer size="6"/>
        <Stack alignItems="center" direction="row">
          <Text category="title-medium">
            Points Expiration Date
          </Text>
          <HSpacer size="3"/>
          <InfoTooltip>
            Select the date that the points rewarded by the program will expire
          </InfoTooltip>
        </Stack>
        <VSpacer size="6"/>
        {latestProcessedOrderDate === undefined ? (
          <CircularProgress/>
        ) : PointsExpirationDateInputs}
        <VSpacer size="6"/>
        <Stack alignItems="center" direction="row">
          <Text category="title-medium">
            Category Mapping
          </Text>
        </Stack>
        <VSpacer size="6"/>
        {CategoryMappingSelect}
        <VSpacer size="6"/>
        <Divider/>
        <VSpacer size="6"/>
        <Stack alignItems="center" direction="row">
          <Text category="title-medium">
            Segments Included
          </Text>
          <HSpacer size="3" />
          <InfoTooltip>
            Please select only the categories and subcategories that are included within the
            retailer rewards program. When setting up the program tiers, you will establish the
            loyalty value for each. If you deselect a category or subcategory then loyalty points
            will not be granted for products purchased in those segments.
          </InfoTooltip>
        </Stack>
        <VSpacer size="6" />
        {CategorySelect}
        <VSpacer size="6" />
        {retailer?.isErpIntegrationEnabled && (
          <>
            <Stack alignItems="center" direction="row">
              <Text category="title-medium">
                Grain source transaction type
              </Text>
              <HSpacer size="3" />
              <InfoTooltip>
                Select the type of grain transaction data to be used for rewards program
                calculations
              </InfoTooltip>
            </Stack>
            <VSpacer size="6" />
            <Select
              disabled={isViewing || hasActiveUsers}
              label="Grain source transaction type"
              onChangeValue={(value) => (
                setProgram({
                  ...program,
                  grainTransactionImportType: value as GrainTransactionImportType,
                })
              )}
              placeholder="Select transaction type"
              testID="grain-transaction-type-select"
              value={program.grainTransactionImportType ?? ''}
              width={464}
            >
              <MenuItem testID="settlements-item" value={GrainTransactionImportType.Settlements}>
                Settlements
              </MenuItem>
              <MenuItem
                testID="scale-tickets-item"
                value={GrainTransactionImportType.ScaleTickets}
              >
                Scale Tickets
              </MenuItem>
              <MenuItem testID="neither-item" value={GrainTransactionImportType.None}>
                Neither
              </MenuItem>
            </Select>
            <VSpacer size="6" />
          </>
        )}
        <Divider />
        <VSpacer size="6" />
        <Text category="body-xlarge">Points granted (optional)</Text>
        <VSpacer size="3" />
        <Checkbox
          checked={program.grantBothProgramAndPromotionPoints}
          disabled={isViewing}
          onChangeChecked={(checked) => (
            setProgram({ ...program, grantBothProgramAndPromotionPoints: checked })
          )}
          sx={{ marginLeft: '12px' }}
          testID="grant-both-program-and-promotion-points-checkbox"
        >
          <Text category="body-large">
            Grant both rewards program loyalty points AND promotion points on eligible products.
          </Text>
        </Checkbox>
        <VSpacer size="6" />
        <Divider />
        <VSpacer size="6" />
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack>
            <Stack alignItems="center" direction="row">
              <Text category="title-medium">
                Tiers
              </Text>
              <HSpacer size="3" />
              <InfoTooltip>
                Create the rewards tiers each customer can earn through loyalty purchases
                with this retailer
              </InfoTooltip>
            </Stack>
          </Stack>
          {!!categories && (
            <Button
              disabled={isViewing || hasActiveUsers}
              onClick={() => setShowRewardsTiersModal(true)}
              startIcon={<Add />}
              testID="tiers-button"
              variant={program.tiers.length ? "text" : "contained"}
            >
              {program.tiers.length ? "Edit Tiers" : "Add Tiers"}
            </Button>
          )}
        </Stack>
        <VSpacer size="6" />
        <Stack>
          {!!categories && tiersWithSelectedCategories.map((tier, index) => (
            <Stack key={tier.name}>
              <RewardsTier
                categories={categories}
                categoryExclusions={{
                  categoryIdsExcludedFromSegmentSpend:
                    program.categoryIdsExcludedFromSegmentSpend,
                  categoryIdsExcludedFromTierSpend: program.categoryIdsExcludedFromTierSpend,
                }}
                rank={index + 1}
                tier={tier}
              />
              <VSpacer size='4' />
            </Stack>
          ))}
        </Stack>
        {!!selectedCategories.length && (
          <TierExclusionsSection
            categoryIdsExcludedFromSegmentSpend={program.categoryIdsExcludedFromSegmentSpend}
            categoryIdsExcludedFromTierSpend={program.categoryIdsExcludedFromTierSpend}
            onSave={(exclusions) => setProgram({
              ...program,
              ...exclusions,
            })}
            readonly={isViewing || hasActiveUsers}
            selectedCategories={selectedCategories}
          />
        )}
        <VSpacer size="9" />
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack>
            <Stack alignItems="center" direction="row">
              <Text category="title-medium">
                Custom Benefits
              </Text>
              <HSpacer size="3" />
              <InfoTooltip>
                Custom benefits are offered to the farmer by the retailer, in addition to tier
                loyalty values, as part of a whole ‘package’ of benefits per tier.
              </InfoTooltip>
            </Stack>
          </Stack>
          {!isViewing && !!program.tiers.length && (
            <Button
              onClick={() => setShowRewardsTierBenefitsModal(true)}
              startIcon={<Add />}
              testID="tiers-button"
              variant="outlined"
            >
              Add Benefit
            </Button>
          )}
        </Stack>
        <VSpacer size="6" />
        <TierBenefitsTable />
      </Modal>
      {showRewardsTiersModal && !!categories && (
        <RewardsTiersModal
          categories={selectedCategories}
          existingTiers={tiersWithSelectedCategories}
          onClose={() => setShowRewardsTiersModal(false)}
          onSave={(tiers) => setProgram({ ...program, tiers })}
        />
      )}
      {showRewardsTierBenefitsModal && !!program.tiers.length && (
        <RewardsTierBenefitModal
          benefit={benefitToEdit}
          onClose={() => setShowRewardsTierBenefitsModal(false)}
          onSave={(benefitSave) => {
            const updatedBenefits = [...benefits];
            if (benefitToEdit) {
              const index = benefits.indexOf(benefitToEdit);
              updatedBenefits[index] = benefitSave;
            } else {
              updatedBenefits.push(benefitSave);
            }
            setBenefits(updatedBenefits);
            setBenefitToEdit(undefined);
          }}
          tiers={tiersWithSelectedCategories}
        />
      )}
      {showDeleteBenefitDialog && benefitToEdit && (
        <DeleteBenefitConfirmDialog
          onCancel={() => setShowDeleteBenefitDialog(false)}
          onConfirm={() => {
            const updatedBenefits =  [...benefits];
            setShowDeleteBenefitDialog(false);
            const index = updatedBenefits.indexOf(benefitToEdit);
            updatedBenefits.splice(index, 1);
            setBenefitToEdit(undefined);
            setBenefits(updatedBenefits);
          }}
          open
        />
      )}
    </>
  );
};
